import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import {
  Card, CardBody, CardTitle, Table, Badge, Button, Input, Label, Modal, ModalHeader,
  ModalBody, ModalFooter, Spinner, Alert,
} from "reactstrap"
import { withTranslation } from "react-i18next"

import {
  patchServiceValidationPod,
  putServiceValidationPodImage,
  postServiceValidationPod,
  patchServiceValidationPodType,
  delServiceValidationPod,
} from "../../helpers/api_rombo_helper"
import { initialDragState, beginDrag, panScrollPosition, markHintSeen } from "./panHint"
import { stepIndex, clampPosition, defaultPosition, assignableTypes, canChangeType } from "./podViewerNav"
import { initMarquee, beginMarquee, updateMarquee, marqueeZoom, isPanGesture } from "./marqueeZoom"

// Panel de gestión de evidencias (PODs) en la validación (SV2-T10).
// - Aprobar (1 clic) / Rechazar (2 clics: motivo del catálogo + texto si "otro").
// - Editar monto (value).
// - Reemplazar imagen (multipart -> vuelve a PENDING en el backend).
// - Indicador de bloqueo de validación si hay PODs no aprobados.
// Fuente de datos: pods[] del servicio (uuid/type/status/url/value/rejected_reason).
const REJECT_REASONS = [
  { value: "imagen_borrosa", labelKey: "PodReasonBlurry" },
  { value: "monto_invalido", labelKey: "PodReasonInvalidAmount" },
  { value: "no_aplica", labelKey: "PodReasonNotApplicable" },
  { value: "otro", labelKey: "PodReasonOther" },
]

// Tipos de evidencia que se pueden dar de alta (catálogo PODType del backend).
// Hay como máximo una evidencia por tipo (constraint trip_uuid_fk+type), EXCEPTO
// OTHER (evidencia genérica), que puede repetirse.
const POD_TYPES = ["TICKET", "TOLL", "PARKING", "OTHER"]
const POD_TYPE_MULTI = "OTHER"

// Tipos de evidencia que exigen un monto mayor a 0 para poder aprobarse.
// En el dominio TOLL = Caseta/Peaje y PARKING = Estacionamiento. Coherente con
// la validación del backend (AMOUNT_REQUIRED_POD_TYPES en PodService).
const AMOUNT_REQUIRED_POD_TYPES = ["TOLL", "PARKING"]

// Preferencias del visor anclado (solo por sesión del navegador).
const VIEWER_PREFS_KEY = "sv2:podviewer"
const FLOAT_SIZE = { width: 480, height: 640 }
const readViewerPrefs = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(VIEWER_PREFS_KEY)) || {}
  } catch (e) {
    return {}
  }
}
const writeViewerPrefs = (prefs) => {
  try {
    window.sessionStorage.setItem(VIEWER_PREFS_KEY, JSON.stringify(prefs))
  } catch (e) { /* storage no disponible: se ignora */ }
}

const statusColor = (s) =>
  s === "APPROVED" ? "success" : s === "REJECTED" ? "danger" : "secondary"

const ServiceValidationPods = ({ t, code, pods, canWrite, heldByMe = false, authHeader, onChanged, embedded = false }) => {
  // Evidencia abierta en el visor (por id, para sobrevivir a las recargas).
  const [previewId, setPreviewId] = useState(null)
  // Visor anclado: panel flotante sin bloqueo, arrastrable por la cabecera.
  const [pinned, setPinned] = useState(() => !!readViewerPrefs().pinned)
  const [pos, setPos] = useState(() => {
    const saved = readViewerPrefs().pos
    if (saved && typeof saved.x === "number" && typeof saved.y === "number") return saved
    return defaultPosition(FLOAT_SIZE, { width: window.innerWidth, height: window.innerHeight })
  })
  const floatRef = useRef(null)
  const floatDragRef = useRef(null)
  // Ctrl mantenido: solo para cambiar el cursor a "grab" (el gesto usa e.ctrlKey).
  const [ctrlHeld, setCtrlHeld] = useState(false)
  // Transformación del visor: zoom (escala) y rotación (grados).
  const [imgZoom, setImgZoom] = useState(1)
  const [imgRot, setImgRot] = useState(0)
  // Tamaño natural de la imagen abierta: define la proporción de la caja que
  // se desplaza (zoom/rotación) para que todo el contenido sea alcanzable.
  const [natSize, setNatSize] = useState(null)
  // Pan por arrastre del ratón dentro del visor: ref al contenedor scrollable y
  // estado efímero del arrastre (no provoca re-render; se guarda en un ref).
  const panViewportRef = useRef(null)
  const panDragRef = useRef(initialDragState())
  // Pista de descubrimiento (glow): visible al abrir el preview, se atenúa tras
  // la primera interacción de marquee/pan/scroll/zoom.
  const [panHintSeen, setPanHintSeen] = useState(false)
  // Marquee zoom (zoom por selección de zona) = gesto PRINCIPAL. Estado efímero
  // del arrastre del rectángulo en un ref (no re-render) y el rectángulo "en
  // vivo" en estado React para pintar el overlay mientras se dibuja.
  const marqueeRef = useRef(initMarquee())
  const [marqueeRect, setMarqueeRect] = useState(null)
  // Barra espaciadora mantenida: activa el pan como gesto SECUNDARIO. En un ref
  // para que los handlers de ratón lean el valor actual sin re-render.
  const spaceHeldRef = useRef(false)
  const openPreview = (p) => { setImgZoom(1); setImgRot(0); setPanHintSeen(false); setMarqueeRect(null); setNatSize(null); setPreviewId(podId(p)) }
  const closePreview = () => setPreviewId(null)

  // Marca la pista como vista en la primera interacción real (marquee, arrastre,
  // rueda o zoom).
  const dismissPanHint = () => { setPanHintSeen(markHintSeen) }

  // Escucha la barra espaciadora solo mientras el preview está abierto, para
  // activar el pan secundario sin robar el gesto por defecto (marquee).
  // También: Ctrl (cursor de pan) y flechas ←/→ para navegar entre evidencias.
  useEffect(() => {
    if (!previewId) return undefined
    const isTyping = (el) => {
      const tag = (el?.tagName || "").toUpperCase()
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || !!el?.isContentEditable
    }
    const onKeyDown = (e) => {
      if (e.code === "Space") spaceHeldRef.current = true
      if (e.key === "Control") setCtrlHeld(true)
      if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && !isTyping(e.target)) {
        e.preventDefault()
        navRef.current(e.key === "ArrowRight" ? 1 : -1)
      }
    }
    const onKeyUp = (e) => {
      if (e.code === "Space") spaceHeldRef.current = false
      if (e.key === "Control") setCtrlHeld(false)
    }
    const onBlur = () => { spaceHeldRef.current = false; setCtrlHeld(false) }
    window.addEventListener("keydown", onKeyDown)
    window.addEventListener("keyup", onKeyUp)
    window.addEventListener("blur", onBlur)
    return () => {
      window.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("keyup", onKeyUp)
      window.removeEventListener("blur", onBlur)
      spaceHeldRef.current = false
      setCtrlHeld(false)
    }
  }, [previewId])

  // Mantiene el panel anclado dentro del viewport al redimensionar la ventana.
  useEffect(() => {
    if (!pinned) return undefined
    const onResize = () => setPos((p) => clampPosition(
      p, { width: floatRef.current?.offsetWidth || FLOAT_SIZE.width, height: FLOAT_SIZE.height },
      { width: window.innerWidth, height: window.innerHeight },
    ))
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [pinned])

  const togglePin = () => {
    const next = !pinned
    setPinned(next)
    writeViewerPrefs({ ...readViewerPrefs(), pinned: next, pos })
  }

  // Arrastre del panel anclado por la cabecera (pointer events + captura).
  const onFloatPointerDown = (e) => {
    if (e.button !== 0 || e.target.closest("button, input, select, a, label")) return
    e.currentTarget.setPointerCapture(e.pointerId)
    floatDragRef.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y }
  }
  const onFloatPointerMove = (e) => {
    const drag = floatDragRef.current
    if (!drag) return
    setPos(clampPosition(
      { x: e.clientX - drag.dx, y: e.clientY - drag.dy },
      { width: floatRef.current?.offsetWidth || FLOAT_SIZE.width, height: FLOAT_SIZE.height },
      { width: window.innerWidth, height: window.innerHeight },
    ))
  }
  const onFloatPointerUp = () => {
    if (!floatDragRef.current) return
    floatDragRef.current = null
    writeViewerPrefs({ ...readViewerPrefs(), pinned, pos })
  }

  const onViewportMouseDown = (e) => {
    const el = panViewportRef.current
    if (!el) return
    dismissPanHint()
    // Gesto secundario: pan con botón central o barra espaciadora mantenida.
    if (isPanGesture(e, spaceHeldRef.current)) {
      panDragRef.current = beginDrag(el, e)
      return
    }
    // Gesto por defecto: marquee (dibujar recuadro para hacer zoom a la zona).
    e.preventDefault()
    marqueeRef.current = beginMarquee(el, e)
    setMarqueeRect({ left: marqueeRef.current.startX, top: marqueeRef.current.startY, width: 0, height: 0 })
  }

  const onViewportMouseMove = (e) => {
    const el = panViewportRef.current
    if (!el) return
    const drag = panDragRef.current
    if (drag.dragging) {
      e.preventDefault()
      const next = panScrollPosition(drag, e)
      el.scrollLeft = next.scrollLeft
      el.scrollTop = next.scrollTop
      return
    }
    if (marqueeRef.current.dragging) {
      e.preventDefault()
      setMarqueeRect(updateMarquee(marqueeRef.current, el, e))
    }
  }

  const onViewportMouseUp = () => {
    const el = panViewportRef.current
    panDragRef.current.dragging = false
    if (!marqueeRef.current.dragging || !el) { setMarqueeRect(null); return }
    marqueeRef.current.dragging = false
    const rect = marqueeRect
    setMarqueeRect(null)
    const out = marqueeZoom({
      rect,
      viewport: { vw: el.clientWidth, vh: el.clientHeight },
      zoom: imgZoom,
      minZoom: 0.25,
      maxZoom: 5,
    })
    if (!out.apply) return
    setImgZoom(out.zoom)
    // El scroll se fija tras el re-render, cuando el <img> ya tiene el nuevo
    // tamaño (si se fijara ahora, el navegador lo recortaría al tamaño viejo).
    requestAnimationFrame(() => {
      const node = panViewportRef.current
      if (!node) return
      node.scrollLeft = out.scrollLeft
      node.scrollTop = out.scrollTop
    })
  }

  // Doble click = volver a la vista completa (zoom-out), reutilizando el estado
  // de zoom existente (no se duplica).
  const resetZoom = () => {
    setImgZoom(1)
    setImgRot(0)
    const el = panViewportRef.current
    if (el) { el.scrollLeft = 0; el.scrollTop = 0 }
  }

  // Ver la imagen en PANTALLA COMPLETA: usa la Fullscreen API sobre el viewport
  // de la imagen (funciona tanto en el visor modal como en el flotante). Si el
  // navegador no la soporta, cae a abrir la imagen en una pestaña nueva.
  const openFullscreen = () => {
    const el = panViewportRef.current
    if (el && el.requestFullscreen) {
      el.requestFullscreen().catch(() => openImageInNewTab())
    } else if (el && el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen()
    } else {
      openImageInNewTab()
    }
  }

  // Abrir la imagen de la evidencia en una PESTAÑA NUEVA (tamaño original, con
  // el zoom nativo del navegador). noopener/noreferrer por seguridad.
  const openImageInNewTab = () => {
    if (preview?.url) window.open(preview.url, "_blank", "noopener,noreferrer")
  }

  const endViewportDrag = () => {
    panDragRef.current.dragging = false
    if (marqueeRef.current.dragging) { marqueeRef.current.dragging = false; setMarqueeRect(null) }
  }
  const podId = (p) => p.uuid || p.id
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState(null)
  // Estado del modal de rechazo.
  const [rejectPod, setRejectPod] = useState(null)
  const [rejectReason, setRejectReason] = useState("imagen_borrosa")
  const [rejectText, setRejectText] = useState("")
  // Estado del modal de eliminación de evidencia (acción destructiva).
  const [deletePod, setDeletePod] = useState(null)
  // Edición de monto inline.
  const [amountEdits, setAmountEdits] = useState({})
  // Estado del modal de alta de evidencia.
  const [addOpen, setAddOpen] = useState(false)
  const [addType, setAddType] = useState("")
  const [addValue, setAddValue] = useState("")
  const [addFile, setAddFile] = useState(null)
  const [adding, setAdding] = useState(false)

  const list = pods || []
  // Evidencias con imagen: las que se pueden recorrer en el visor.
  const viewable = list.filter((p) => p.url)
  const preview = previewId ? list.find((p) => podId(p) === previewId) || null : null
  const previewIndex = preview ? viewable.findIndex((p) => podId(p) === previewId) : -1
  const goTo = (dir) => {
    const idx = stepIndex(viewable.length, previewIndex, dir)
    if (idx >= 0) openPreview(viewable[idx])
  }
  const navRef = useRef(goTo)
  navRef.current = goTo
  const pendingCount = list.filter((p) => (p.status || "").toUpperCase() !== "APPROVED").length


  // Tipos disponibles para alta: los que aún no existen en el servicio, más
  // OTHER que siempre se puede agregar (admite múltiples).
  const existingTypes = list.map((p) => (p.type || "").toUpperCase())
  const availableTypes = POD_TYPES.filter(
    (ty) => ty === POD_TYPE_MULTI || !existingTypes.includes(ty),
  )

  const openAdd = () => {
    setError(null)
    setAddType(availableTypes[0] || "")
    setAddValue("")
    setAddFile(null)
    setAddOpen(true)
  }

  const confirmAdd = async () => {
    if (!canWrite || !addType || !addFile) return
    setError(null); setAdding(true)
    try {
      const token = await authHeader()
      const formData = new FormData()
      formData.append("type", addType)
      if (addValue !== "") formData.append("value", String(addValue))
      formData.append("file", addFile)
      await postServiceValidationPod(token, code, formData)
      setAddOpen(false)
      onChanged && (await onChanged())
    } catch (e) {
      setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setAdding(false)
    }
  }

  const approve = async (p) => {
    if (!canWrite) return
    const amount = amountEdits[podId(p)]
    // Monto efectivo: edición inline si existe, de lo contrario el valor actual
    // del POD. Para Caseta/Peaje (TOLL) y Estacionamiento (PARKING) debe ser > 0.
    const effectiveAmount =
      amount !== undefined && amount !== "" ? amount : p.value
    if (AMOUNT_REQUIRED_POD_TYPES.includes((p.type || "").toUpperCase())) {
      const numeric = Number(effectiveAmount)
      if (
        effectiveAmount === null ||
        effectiveAmount === undefined ||
        effectiveAmount === "" ||
        Number.isNaN(numeric) ||
        numeric <= 0
      ) {
        setError(t("PodAmountRequiredForType"))
        return
      }
    }
    const id = podId(p)
    if (!id) { setError(t("ValidationError")); return }
    setError(null); setBusyId(id)
    try {
      const token = await authHeader()
      const args = { status: "APPROVED" }
      if (amount !== undefined && amount !== "") args.value = String(amount)
      await patchServiceValidationPod(token, code, id, args)
      onChanged && (await onChanged())
    } catch (e) {
      setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setBusyId(null)
    }
  }

  const openReject = (p) => {
    setRejectPod(p)
    setRejectReason("imagen_borrosa")
    setRejectText("")
  }

  const confirmReject = async () => {
    if (!rejectPod) return
    const id = podId(rejectPod)
    if (!id) { setError(t("ValidationError")); return }
    setError(null); setBusyId(id)
    try {
      const token = await authHeader()
      const args = { status: "REJECTED", reject_reason: rejectReason }
      if (rejectReason === "otro") args.reject_reason_text = rejectText
      await patchServiceValidationPod(token, code, id, args)
      setRejectPod(null)
      onChanged && (await onChanged())
    } catch (e) {
      setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setBusyId(null)
    }
  }

  const openDelete = (p) => setDeletePod(p)

  // Elimina (soft-delete) la evidencia tras confirmar. El backend es idempotente
  // (200 también al re-borrar) y ya excluye las evidencias borradas del detalle,
  // así que basta con refrescar vía onChanged para que desaparezca de la lista.
  const confirmDelete = async () => {
    if (!deletePod) return
    const id = podId(deletePod)
    if (!id) { setError(t("ValidationError")); return }
    setError(null); setBusyId(id)
    try {
      const token = await authHeader()
      await delServiceValidationPod(token, code, id)
      setDeletePod(null)
      onChanged && (await onChanged())
    } catch (e) {
      setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setBusyId(null)
    }
  }

  const changeType = async (p, newType) => {
    if (!canChangeType(p, canWrite, heldByMe) || !newType || newType === (p.type || "").toUpperCase()) return
    const id = podId(p)
    if (!id) { setError(t("ValidationError")); return }
    setError(null); setBusyId(id)
    try {
      const token = await authHeader()
      await patchServiceValidationPodType(token, code, id, { type: newType })
      onChanged && (await onChanged())
    } catch (e) {
      setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setBusyId(null)
    }
  }

  // Selector de tipo (solo evidencias pendientes con permiso y hold).
  const renderTypeSelect = (p, busy) => (
    <Input
      type="select" bsSize="sm" value={(p.type || "").toUpperCase()}
      disabled={busy} title={t("ChangeType")}
      onChange={(e) => changeType(p, e.target.value)}
    >
      {assignableTypes(POD_TYPES, POD_TYPE_MULTI, (p.type || "").toUpperCase(), existingTypes).map((ty) => (
        <option key={ty} value={ty}>{t(ty) || ty}</option>
      ))}
    </Input>
  )

  const replaceImage = async (p, file) => {
    if (!canWrite || !file) return
    const id = podId(p)
    if (!id) { setError(t("ValidationError")); return }
    setError(null); setBusyId(id)
    try {
      const token = await authHeader()
      const formData = new FormData()
      formData.append("file", file)
      await putServiceValidationPodImage(token, code, id, formData)
      onChanged && (await onChanged())
    } catch (e) {
      setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setBusyId(null)
    }
  }

  const renderViewerHeader = (floating) => {
    const st = (preview.status || "").toUpperCase()
    const dragProps = floating
      ? { onPointerDown: onFloatPointerDown, onPointerMove: onFloatPointerMove, onPointerUp: onFloatPointerUp, onPointerCancel: onFloatPointerUp }
      : {}
    return (
      <div className={`modal-header py-2 ${floating ? "sv-pod-float-header" : ""}`} {...dragProps}>
        <div className="d-flex align-items-center gap-1 flex-grow-1">
          <Button color="light" size="sm" disabled={viewable.length < 2} onClick={() => goTo(-1)} title={t("PrevEvidence")}>
            <i className="bx bx-chevron-left" />
          </Button>
          <span className="small text-muted" style={{ minWidth: 36, textAlign: "center" }}>
            {previewIndex + 1}/{viewable.length}
          </span>
          <Button color="light" size="sm" disabled={viewable.length < 2} onClick={() => goTo(1)} title={t("NextEvidence")}>
            <i className="bx bx-chevron-right" />
          </Button>
          <h6 className="modal-title mb-0 ms-2">
            {`${t(preview.type) || preview.type} · `}<Badge color={statusColor(st)}>{t(st) || st}</Badge>
          </h6>
        </div>
        <Button color="light" size="sm" className="me-1" onClick={openFullscreen}
          title={t("Fullscreen") || "Pantalla completa"}>
          <i className="bx bx-fullscreen" />
        </Button>
        <Button color="light" size="sm" className="me-1" onClick={openImageInNewTab}
          title={t("OpenInNewTab") || "Abrir en pestaña nueva"}>
          <i className="bx bx-link-external" />
        </Button>
        <Button color={pinned ? "primary" : "light"} size="sm" className="me-1" onClick={togglePin}
          title={pinned ? t("UnpinViewer") : t("PinViewer")}>
          <i className={`bx ${pinned ? "bxs-pin" : "bx-pin"}`} />
        </Button>
        <button type="button" className="btn-close" aria-label={t("CloseViewer")} onClick={closePreview} />
      </div>
    )
  }

  const renderViewerBody = () => {
    const id = podId(preview)
    const st = (preview.status || "").toUpperCase()
    const busy = busyId === id
    return (
      <div className="modal-body text-center">
        {error && <Alert color="danger" className="py-2 text-start">{error}</Alert>}
        {/* Controles: zoom -/+, reset, rotar izq/der. */}
        <div className="d-flex justify-content-center gap-1 mb-2">
          <Button color="light" size="sm" onClick={() => { dismissPanHint(); setImgZoom((z) => Math.max(0.25, +(z - 0.25).toFixed(2))) }}
            title={t("ZoomOut") || "Alejar"}><i className="bx bx-zoom-out" /></Button>
          <span className="align-self-center small text-muted" style={{ minWidth: 44 }}>
            {Math.round(imgZoom * 100)}%
          </span>
          <Button color="light" size="sm" onClick={() => { dismissPanHint(); setImgZoom((z) => Math.min(5, +(z + 0.25).toFixed(2))) }}
            title={t("ZoomIn") || "Acercar"}><i className="bx bx-zoom-in" /></Button>
          <Button color="light" size="sm" onClick={() => setImgRot((r) => (r - 90 + 360) % 360)}
            title={t("RotateLeft") || "Girar izquierda"}><i className="bx bx-rotate-left" /></Button>
          <Button color="light" size="sm" onClick={() => setImgRot((r) => (r + 90) % 360)}
            title={t("RotateRight") || "Girar derecha"}><i className="bx bx-rotate-right" /></Button>
          <Button color="light" size="sm" onClick={() => { dismissPanHint(); resetZoom() }}
            title={t("ResetZoom") || "Ver imagen completa"}><i className="bx bx-reset" /></Button>
        </div>
        <div
          ref={panViewportRef}
          className={!panHintSeen ? "sv-pan-hint" : undefined}
          title={t("DragToZoomAreaOrPan")}
          aria-label={t("DragToZoomAreaOrPan")}
          onContextMenu={(e) => { if (e.ctrlKey) e.preventDefault() }}
          onMouseDown={onViewportMouseDown}
          onMouseMove={onViewportMouseMove}
          onMouseUp={onViewportMouseUp}
          onMouseLeave={endViewportDrag}
          onDoubleClick={() => { dismissPanHint(); resetZoom() }}
          onWheel={dismissPanHint}
          style={{
            position: "relative",
            overflow: "auto",
            maxHeight: pinned ? "50vh" : "70vh",
            cursor: panDragRef.current.dragging ? "grabbing" : ctrlHeld ? "grab" : "crosshair",
          }}
        >
          {preview.url && (() => {
            // La caja de la imagen se dimensiona de verdad (ancho = zoom × visor,
            // proporción según rotación) en vez de usar transform: scale, que
            // dejaba parte de la imagen fuera del área desplazable. La imagen
            // rota sobre su centro dentro de esa caja.
            const quarter = imgRot % 180 !== 0
            const loaded = !!natSize
            const boxRatio = loaded
              ? (quarter ? `${natSize.h} / ${natSize.w}` : `${natSize.w} / ${natSize.h}`)
              : undefined
            const imgWidthPct = loaded && quarter ? (100 * natSize.w) / natSize.h : 100
            return (
              <div
                style={{
                  position: "relative",
                  width: `${imgZoom * 100}%`,
                  aspectRatio: boxRatio,
                  margin: "0 auto",
                  overflow: loaded ? "visible" : undefined,
                }}
              >
                <img
                  src={preview.url}
                  alt={preview.type}
                  draggable={false}
                  onLoad={(e) => setNatSize({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                  style={loaded ? {
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    width: `${imgWidthPct}%`,
                    height: "auto",
                    // Anula la regla global `.modal-body img { max-width/height }`
                    // (app.scss): al hacer zoom recortaba la altura a 70vh sin
                    // recortar el ancho, deformando la proporción. La proporción
                    // la controla la caja (aspectRatio) + width/height:auto.
                    maxWidth: "none",
                    maxHeight: "none",
                    transform: `translate(-50%, -50%) rotate(${imgRot}deg)`,
                    userSelect: "none",
                    pointerEvents: "none",
                  } : {
                    display: "block", width: "100%", height: "auto",
                    maxWidth: "none", maxHeight: "none",
                    userSelect: "none", pointerEvents: "none",
                  }}
                />
              </div>
            )
          })()}
          {marqueeRect && marqueeRect.width > 0 && marqueeRect.height > 0 && (
            <div
              className="sv-marquee-rect"
              style={{
                left: marqueeRect.left,
                top: marqueeRect.top,
                width: marqueeRect.width,
                height: marqueeRect.height,
              }}
            />
          )}
        </div>
        <small className="text-muted d-block mt-1">
          <i className="bx bx-move me-1" />{t("PanWithCtrlHint")}
        </small>

        {/* Acciones sobre la evidencia desde el visor */}
        {canWrite && (
          <div className="mt-3 text-start">
            {!heldByMe && (
              <Alert color="warning" className="py-2">
                <i className="bx bx-lock-alt me-1" /> {t("TakeHoldFirst")}
              </Alert>
            )}
            <div className="d-flex flex-wrap gap-2 align-items-end">
              {canChangeType(preview, canWrite, heldByMe) && (
                <div style={{ minWidth: 150 }}>
                  <Label className="small mb-0">{t("Type")}</Label>
                  {renderTypeSelect(preview, busy)}
                </div>
              )}
              <div style={{ width: 120 }}>
                <Label className="small mb-0">{t("Amount")}</Label>
                <Input
                  bsSize="sm" type="number"
                  value={amountEdits[id] ?? preview.value ?? ""}
                  onChange={(e) => setAmountEdits((m) => ({ ...m, [id]: e.target.value }))}
                />
              </div>
              <Button color="success" size="sm" disabled={busy || st === "APPROVED" || !heldByMe}
                onClick={() => approve(preview)} title={!heldByMe ? t("TakeHoldFirst") : t("Approve")}>
                {busy ? <Spinner size="sm" /> : <i className="bx bx-check" />} {t("Approve")}
              </Button>
              <Button color="outline-danger" size="sm" disabled={busy || st === "REJECTED" || !heldByMe}
                onClick={() => openReject(preview)} title={!heldByMe ? t("TakeHoldFirst") : t("Reject")}>
                <i className="bx bx-x" /> {t("Reject")}
              </Button>
              <Label className="btn btn-outline-secondary btn-sm mb-0" title={t("ReplaceImage")}>
                <i className="bx bx-upload" /> {t("ReplaceImage")}
                <input
                  type="file" accept="image/*" hidden
                  onChange={(e) => { replaceImage(preview, e.target.files?.[0]); e.target.value = "" }}
                />
              </Label>
              <Button color="outline-danger" size="sm" disabled={busy || !heldByMe}
                onClick={() => openDelete(preview)} title={!heldByMe ? t("TakeHoldFirst") : t("DeleteEvidence")}>
                <i className="bx bx-trash" /> {t("DeleteEvidence")}
              </Button>
            </div>
            {st === "REJECTED" && preview.rejected_reason && (
              <div className="small text-muted mt-1">{preview.rejected_reason}</div>
            )}
          </div>
        )}
      </div>
    )
  }

  const body = (
    <>
        <div className="d-flex justify-content-between align-items-center mb-2">
          {embedded ? <span /> : <CardTitle tag="h5" className="mb-0">{t("Evidence") || "Evidencias"}</CardTitle>}
          <div className="d-flex align-items-center gap-2">
            {pendingCount > 0 ? (
              <Badge color="warning">
                <i className="bx bx-lock-alt me-1" />
                {pendingCount} {t("PodsPendingBlockValidation")}
              </Badge>
            ) : list.length > 0 ? (
              <Badge color="success"><i className="bx bx-check-circle me-1" />{t("AllPodsApproved")}</Badge>
            ) : null}
            {canWrite && availableTypes.length > 0 && (
              <Button color="light" size="sm" onClick={openAdd}>
                <i className="bx bx-plus me-1" />{t("AddEvidence") || "Agregar evidencia"}
              </Button>
            )}
          </div>
        </div>

        {error && <Alert color="danger" className="py-2">{error}</Alert>}

        {/* Guarda on-hold (PT-4074): no se pueden aprobar/rechazar evidencias si
            el servicio no está tomado por el usuario actual. */}
        {canWrite && !heldByMe && (
          <Alert color="warning" className="py-2">
            <i className="bx bx-lock-alt me-1" /> {t("TakeHoldFirst")}
          </Alert>
        )}

        {list.length === 0 ? (
          <p className="text-muted mb-0">{t("NoResults")}</p>
        ) : (
          <Table className="table table-bordered mb-0" size="sm">
            <thead>
              <tr>
                <th>{t("Name")}</th>
                <th>{t("Status")}</th>
                <th>{t("Amount")}</th>
                <th className="text-center">{t("Image")}</th>
                <th className="text-end">{t("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => {
                const id = podId(p)
                const st = (p.status || "").toUpperCase()
                const busy = busyId === id
                return (
                  <tr key={id}>
                    <td style={canChangeType(p, canWrite, heldByMe) ? { minWidth: 150 } : undefined}>
                      {canChangeType(p, canWrite, heldByMe) ? renderTypeSelect(p, busy) : (t(p.type) || p.type)}
                    </td>
                    <td>
                      <Badge color={statusColor(st)}>{t(st) || st}</Badge>
                      {st === "REJECTED" && p.rejected_reason && (
                        <div className="small text-muted">{p.rejected_reason}</div>
                      )}
                    </td>
                    <td style={{ width: 120 }}>
                      {canWrite ? (
                        <Input
                          bsSize="sm"
                          type="number"
                          value={amountEdits[id] ?? p.value ?? ""}
                          onChange={(e) =>
                            setAmountEdits((m) => ({ ...m, [id]: e.target.value }))
                          }
                        />
                      ) : (
                        p.value ?? "-"
                      )}
                    </td>
                    <td className="text-center">
                      {p.url ? (
                        <button
                          type="button"
                          className="btn btn-link p-0 border-0 position-relative d-inline-block"
                          style={{ lineHeight: 0 }}
                          onClick={() => openPreview(p)}
                          title={t("ClickToZoom") || "Clic para ampliar"}
                        >
                          <img
                            src={p.url}
                            alt={p.type}
                            style={{ height: 44, width: 44, objectFit: "cover", borderRadius: 4, cursor: "zoom-in", border: "1px solid #dee2e6" }}
                          />
                          <span
                            className="position-absolute d-flex align-items-center justify-content-center"
                            style={{
                              inset: 0, borderRadius: 4, cursor: "zoom-in",
                              background: "rgba(0,0,0,0.28)", color: "#fff", opacity: 0.9,
                            }}
                          >
                            <i className="bx bx-zoom-in" style={{ fontSize: 18 }} />
                          </span>
                        </button>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-end">
                      {canWrite && (
                        <div className="d-flex gap-1 justify-content-end align-items-center">
                          <Button
                            color="success" size="sm" disabled={busy || st === "APPROVED" || !heldByMe}
                            onClick={() => approve(p)} title={!heldByMe ? t("TakeHoldFirst") : t("Approve")}
                          >
                            {busy ? <Spinner size="sm" /> : <i className="bx bx-check" />}
                          </Button>
                          <Button
                            color="outline-danger" size="sm" disabled={busy || st === "REJECTED" || !heldByMe}
                            onClick={() => openReject(p)} title={!heldByMe ? t("TakeHoldFirst") : t("Reject")}
                          >
                            <i className="bx bx-x" />
                          </Button>
                          <Label className="btn btn-outline-secondary btn-sm mb-0" title={t("ReplaceImage")}>
                            <i className="bx bx-upload" />
                            <input
                              type="file" accept="image/*" hidden
                              onChange={(e) => replaceImage(p, e.target.files?.[0])}
                            />
                          </Label>
                          <Button
                            color="outline-danger" size="sm" disabled={busy || !heldByMe}
                            onClick={() => openDelete(p)} title={!heldByMe ? t("TakeHoldFirst") : t("DeleteEvidence")}
                          >
                            <i className="bx bx-trash" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}

        {/* Visor de evidencias: modal, o panel flotante sin bloqueo si está anclado */}
        {preview && (pinned
          ? createPortal(
            <div
              ref={floatRef}
              className="sv-pod-float"
              style={{ left: pos.x, top: pos.y }}
            >
              {renderViewerHeader(true)}
              {renderViewerBody()}
            </div>,
            document.body,
          )
          : (
            <Modal isOpen toggle={closePreview} size="lg" centered>
              {renderViewerHeader(false)}
              {renderViewerBody()}
            </Modal>
          ))}

        {/* Modal de alta de evidencia */}
        <Modal isOpen={addOpen} toggle={() => setAddOpen(false)} centered>
          <ModalHeader toggle={() => setAddOpen(false)}>{t("AddEvidence") || "Agregar evidencia"}</ModalHeader>
          <ModalBody>
            <Label>{t("Type") || "Tipo"}</Label>
            <Input type="select" value={addType} onChange={(e) => setAddType(e.target.value)}>
              {availableTypes.map((ty) => (
                <option key={ty} value={ty}>{t(ty) || ty}</option>
              ))}
            </Input>
            <div className="mt-2">
              <Label>{t("Amount") || "Monto"}</Label>
              <Input
                type="number" min="0" step="0.01" value={addValue}
                onChange={(e) => setAddValue(e.target.value)}
                placeholder={t("Optional") || "Opcional"}
              />
            </div>
            <div className="mt-2">
              <Label>{t("Image") || "Imagen"}</Label>
              <Input
                type="file" accept="image/*"
                onChange={(e) => setAddFile(e.target.files?.[0] || null)}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setAddOpen(false)} disabled={adding}>{t("Cancel")}</Button>
            <Button color="primary" onClick={confirmAdd} disabled={adding || !addType || !addFile}>
              {adding ? <Spinner size="sm" /> : (t("Add") || "Agregar")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Modal de rechazo con motivo */}
        <Modal isOpen={!!rejectPod} toggle={() => setRejectPod(null)} centered>
          <ModalHeader toggle={() => setRejectPod(null)}>{t("RejectProof")}</ModalHeader>
          <ModalBody>
            <Label>{t("RejectReason")}</Label>
            <Input type="select" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}>
              {REJECT_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{t(r.labelKey) || r.value}</option>
              ))}
            </Input>
            {rejectReason === "otro" && (
              <div className="mt-2">
                <Label>{t("Detail")}</Label>
                <Input
                  type="textarea" value={rejectText}
                  onChange={(e) => setRejectText(e.target.value)}
                  placeholder={t("PodReasonOtherPlaceholder")}
                />
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setRejectPod(null)}>{t("Cancel")}</Button>
            <Button
              color="danger"
              disabled={rejectReason === "otro" && !rejectText.trim()}
              onClick={confirmReject}
            >
              {t("Reject")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Modal de confirmación para eliminar evidencia (acción destructiva) */}
        <Modal isOpen={!!deletePod} toggle={() => setDeletePod(null)} centered>
          <ModalHeader toggle={() => setDeletePod(null)}>{t("DeleteEvidence")}</ModalHeader>
          <ModalBody>
            <p className="mb-0">{t("ConfirmDeleteEvidence")}</p>
            {deletePod && (
              <div className="small text-muted mt-2">{t(deletePod.type) || deletePod.type}</div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setDeletePod(null)}>{t("Cancel")}</Button>
            <Button
              color="danger"
              disabled={!!deletePod && busyId === podId(deletePod)}
              onClick={confirmDelete}
            >
              {!!deletePod && busyId === podId(deletePod) ? <Spinner size="sm" /> : t("Delete")}
            </Button>
          </ModalFooter>
        </Modal>
    </>
  )

  // Embedded: se usa dentro de un tab (sin Card propia). Standalone: Card.
  if (embedded) return body
  return (
    <Card>
      <CardBody>{body}</CardBody>
    </Card>
  )
}

export default withTranslation()(ServiceValidationPods)
